'use client';

import { c as _c } from "react/compiler-runtime";
import type { ToastContent } from '@/lib/notifications/web-app/send-notifications';
import type { PrV2ChangedDomain, PrV2UpdateScope } from '@/lib/pr-v2/realtime-shared';
import type { PayloadForEventType } from '@/lib/realtime/types';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';
import { usePrNavigation } from '@/components/providers/pr-navigation';
import { useToDesktop } from '@/components/providers/todesktop-auth-provider';
import { REPOSITORY_DATA_SOURCE } from '@/data/installations/constants';
import { installationRepositoriesQueryKey, installationsQueryKey } from '@/data/installations/queries';
import { useInstallations } from '@/data/installations/use-installations';
import { notificationQueryKey } from '@/data/notifications/use-notifications';
import { inboxV2AutoFixSummariesQueryKeyPrefix, inboxV2MergedPullRequestsQueryKeyPrefix, inboxV2OpenPullRequestsQueryKeyPrefix } from '@/data/pull-requests/query-keys';
import { clientLogger } from '@/lib/client-logger';
import { useRealtime } from '@/lib/hooks/use-realtime';
import { catchUpMissedNotifications, showNotificationOnce } from '@/lib/notifications/web-app/missed-notifications';
import { closeToastsAbout } from '@/lib/notifications/web-app/pull-request-toasts';
import { sendElectronOrWebNotification } from '@/lib/notifications/web-app/send-notifications';
import { PR_V2_ACTION, PR_V2_CHANGED_DOMAIN, PR_V2_UPDATE_SCOPE, PR_V2_WEBHOOK_EVENT } from '@/lib/pr-v2/realtime-shared';
import { REALTIME, REALTIME_KEYS } from '@/lib/realtime/types';
import { useInstallationStore } from '@/lib/stores/installation-store';
import { useAuth } from '@/providers/auth-provider';
import { buildPrPath } from '@/utils/app-paths';

// Inbox cards depend on cached summary/review state, not per-comment discussion churn.
const INBOX_V2_REALTIME_REFRESH_DOMAINS = new Set<PrV2ChangedDomain>([PR_V2_CHANGED_DOMAIN.SUMMARY, PR_V2_CHANGED_DOMAIN.REVIEWS, PR_V2_CHANGED_DOMAIN.CUBIC_REVIEW]);
// Webhook projection and reconcile can emit multiple identical PR update hints in quick
// succession. Coalesce them per PR so the inbox surfaces refetch once per burst instead of
// repeatedly refetching the same open / merged sections.
const INBOX_REALTIME_INVALIDATION_COOLDOWN_MS = 5_000;
const MAX_TRACKED_INVALIDATIONS = 250;
type RealtimePrIdentity = {
  owner: string;
  repo: string;
  number: number;
};
type RealtimePrInvalidationScope = PrV2UpdateScope | 'auto-fix';
function shouldRefreshInboxV2(changedDomains: PrV2ChangedDomain[] | undefined): boolean {
  if (!changedDomains || changedDomains.length === 0) {
    return true;
  }
  return changedDomains.some(domain => INBOX_V2_REALTIME_REFRESH_DOMAINS.has(domain));
}
function shouldRefreshInboxV2MergedSection(event: Pick<PayloadForEventType<typeof REALTIME_KEYS.PR_V2_UPDATE>, 'webhookEvent' | 'action'>): boolean {
  if (event.action === PR_V2_ACTION.MERGED || event.action === PR_V2_ACTION.CLOSED) {
    return true;
  }
  if (event.webhookEvent === PR_V2_WEBHOOK_EVENT.REVIEW_STATUS) {
    return true;
  }
  return event.webhookEvent === PR_V2_WEBHOOK_EVENT.SYNC && event.action === PR_V2_ACTION.SUMMARY;
}
function buildRealtimePrKey(pr: RealtimePrIdentity, scope: RealtimePrInvalidationScope = PR_V2_UPDATE_SCOPE.DEFAULT): string {
  return `${pr.owner}/${pr.repo}#${pr.number}:${scope}`;
}
function pruneRecentInvalidations(recentInvalidations: Map<string, number>, now: number): void {
  if (recentInvalidations.size <= MAX_TRACKED_INVALIDATIONS) {
    return;
  }
  for (const [key, timestamp] of recentInvalidations.entries()) {
    if (now - timestamp >= INBOX_REALTIME_INVALIDATION_COOLDOWN_MS) {
      recentInvalidations.delete(key);
    }
  }
}
function scheduleCoalescedPrInvalidation(recentInvalidations: Map<string, number>, scheduledInvalidations: Map<string, ReturnType<typeof setTimeout>>, pr: RealtimePrIdentity, onInvalidate: () => void, options?: {
  scope?: RealtimePrInvalidationScope;
  now?: number;
}): void {
  const now = options?.now ?? Date.now();
  const prKey = buildRealtimePrKey(pr, options?.scope);
  const lastInvalidatedAt = recentInvalidations.get(prKey);
  if (lastInvalidatedAt === undefined) {
    recentInvalidations.set(prKey, now);
    pruneRecentInvalidations(recentInvalidations, now);
    onInvalidate();
    return;
  }
  const elapsedMs = now - lastInvalidatedAt;
  if (elapsedMs >= INBOX_REALTIME_INVALIDATION_COOLDOWN_MS) {
    recentInvalidations.set(prKey, now);
    pruneRecentInvalidations(recentInvalidations, now);
    onInvalidate();
    return;
  }
  if (scheduledInvalidations.has(prKey)) {
    return;
  }
  const remainingDelayMs = INBOX_REALTIME_INVALIDATION_COOLDOWN_MS - elapsedMs;
  const timer = setTimeout(() => {
    scheduledInvalidations.delete(prKey);
    const firedAt = Date.now();
    recentInvalidations.set(prKey, firedAt);
    pruneRecentInvalidations(recentInvalidations, firedAt);
    onInvalidate();
  }, remainingDelayMs);
  scheduledInvalidations.set(prKey, timer);
}

/**
 * Component that sets up all realtime subscriptions for the app.
 * This includes:
 * - Installation updates (when users add/remove GitHub installations)
 * - Pull request updates
 * - Test run updates
 * - Notification updates
 */
export function GlobalRealtimeSubscriptions() {
  const $ = _c(50);
  const queryClient = useQueryClient();
  const {
    user
  } = useAuth();
  const pathname = usePathname();
  const {
    navigate
  } = usePrNavigation();
  const {
    isDesktopApp
  } = useToDesktop();
  const selectedInstallationId = useInstallationStore(_temp);
  const userId = user?.id || "anonymous";
  const {
    data: installations
  } = useInstallations();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const recentInboxV2InvalidationsRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scheduledInboxV2InvalidationsRef = useRef(t1);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const scheduledInboxV2Invalidations = scheduledInboxV2InvalidationsRef.current;
      return () => {
        for (const timer of scheduledInboxV2Invalidations.values()) {
          clearTimeout(timer);
        }
        scheduledInboxV2Invalidations.clear();
      };
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== installations) {
    t4 = installations?.map(_temp2) || [];
    $[4] = installations;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const installationIds = t4;
  let t5;
  if ($[6] !== pathname || $[7] !== selectedInstallationId) {
    t5 = (pathname?.startsWith("/inbox") ?? false) && selectedInstallationId !== null;
    $[6] = pathname;
    $[7] = selectedInstallationId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const inboxOwnsPrUpdates = t5;
  let t6;
  let t7;
  if ($[9] !== pathname) {
    t6 = () => {
      if (pathname) {
        closeToastsAbout(pathname);
      }
    };
    t7 = [pathname];
    $[9] = pathname;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== queryClient) {
    t8 = event => {
      if (event.type === REALTIME[REALTIME_KEYS.INSTALLATION_UPDATE].eventType) {
        clientLogger.info(`Installation update received for ID ${event.installationId}, invalidating queries`);
        queryClient.invalidateQueries({
          queryKey: installationsQueryKey()
        });
        queryClient.invalidateQueries({
          queryKey: installationRepositoriesQueryKey(event.installationId, REPOSITORY_DATA_SOURCE.GITHUB),
          exact: false
        });
        queryClient.invalidateQueries({
          queryKey: installationRepositoriesQueryKey(event.installationId, REPOSITORY_DATA_SOURCE.DB),
          exact: false
        });
      }
    };
    $[12] = queryClient;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = Boolean(user?.id) && installationIds.length > 0;
  let t10;
  if ($[14] !== installationIds || $[15] !== t8 || $[16] !== t9) {
    t10 = {
      onEvent: t8,
      eventKey: REALTIME_KEYS.INSTALLATION_UPDATE,
      id: installationIds,
      enabled: t9
    };
    $[14] = installationIds;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  useRealtime(t10);
  let t11;
  if ($[18] !== inboxOwnsPrUpdates || $[19] !== queryClient) {
    t11 = event_0 => {
      if (event_0.type === REALTIME[REALTIME_KEYS.PR_V2_UPDATE].eventType) {
        if (event_0.action === PR_V2_ACTION.MERGED || event_0.action === PR_V2_ACTION.CLOSED) {
          closeToastsAbout(buildPrPath(event_0.pr));
        }
        if (inboxOwnsPrUpdates) {
          return;
        }
        if (event_0.changedDomains?.includes(PR_V2_CHANGED_DOMAIN.AUTO_FIX)) {
          scheduleCoalescedPrInvalidation(recentInboxV2InvalidationsRef.current, scheduledInboxV2InvalidationsRef.current, event_0.pr, () => {
            queryClient.invalidateQueries({
              queryKey: inboxV2AutoFixSummariesQueryKeyPrefix()
            });
          }, {
            scope: "auto-fix"
          });
        }
        if (!shouldRefreshInboxV2(event_0.changedDomains)) {
          return;
        }
        scheduleCoalescedPrInvalidation(recentInboxV2InvalidationsRef.current, scheduledInboxV2InvalidationsRef.current, event_0.pr, () => {
          queryClient.invalidateQueries({
            queryKey: inboxV2OpenPullRequestsQueryKeyPrefix()
          });
          clientLogger.info("GlobalRealtimeSubscriptions: PR v2 update received, invalidating inbox v2", {
            action: event_0.action,
            changedDomains: event_0.changedDomains ?? null,
            pr: event_0.pr,
            webhookEvent: event_0.webhookEvent
          });
        });
        if (shouldRefreshInboxV2MergedSection(event_0)) {
          scheduleCoalescedPrInvalidation(recentInboxV2InvalidationsRef.current, scheduledInboxV2InvalidationsRef.current, event_0.pr, () => {
            queryClient.invalidateQueries({
              queryKey: inboxV2MergedPullRequestsQueryKeyPrefix()
            });
            clientLogger.info("GlobalRealtimeSubscriptions: PR v2 update received, invalidating inbox v2 merged history", {
              action: event_0.action,
              changedDomains: event_0.changedDomains ?? null,
              pr: event_0.pr,
              webhookEvent: event_0.webhookEvent
            });
          }, {
            scope: PR_V2_UPDATE_SCOPE.MERGED
          });
        }
      }
    };
    $[18] = inboxOwnsPrUpdates;
    $[19] = queryClient;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = Boolean(installationIds.length);
  let t13;
  if ($[21] !== installationIds || $[22] !== t11 || $[23] !== t12) {
    t13 = {
      onEvent: t11,
      eventKey: REALTIME_KEYS.PR_V2_UPDATE,
      id: installationIds,
      enabled: t12
    };
    $[21] = installationIds;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  useRealtime(t13);
  let t14;
  if ($[25] !== queryClient || $[26] !== user?.id) {
    t14 = event_1 => {
      if (event_1.type === REALTIME[REALTIME_KEYS.NOTIFICATION_UPDATE].eventType && event_1.userId === user?.id) {
        clientLogger.info("GlobalRealtimeSubscriptions: notification update received, invalidating notifications query");
        queryClient.invalidateQueries({
          queryKey: notificationQueryKey(user?.id)
        });
      }
    };
    $[25] = queryClient;
    $[26] = user?.id;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = Boolean(user?.id);
  let t16;
  if ($[28] !== t14 || $[29] !== t15 || $[30] !== userId) {
    t16 = {
      onEvent: t14,
      eventKey: REALTIME_KEYS.NOTIFICATION_UPDATE,
      id: userId,
      enabled: t15
    };
    $[28] = t14;
    $[29] = t15;
    $[30] = userId;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  useRealtime(t16);
  let t17;
  if ($[32] !== navigate) {
    t17 = toast => sendElectronOrWebNotification(toast, href => navigate(href, {
      source: "notification",
      entrance: "window-reveal"
    }));
    $[32] = navigate;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const showNotification = t17;
  const catchUpSignalRef = useRef(null);
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      const controller = new AbortController();
      catchUpSignalRef.current = controller.signal;
      return () => controller.abort();
    };
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== userId) {
    t19 = [userId];
    $[35] = userId;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  useEffect(t18, t19);
  let t20;
  if ($[37] !== isDesktopApp || $[38] !== showNotification || $[39] !== user) {
    t20 = () => {
      const signal = catchUpSignalRef.current;
      if (!isDesktopApp || !user || !signal) {
        return;
      }
      catchUpMissedNotifications(user.id, showNotification, signal).catch(error => {
        if (!signal.aborted) {
          clientLogger.error("Failed to show missed desktop notifications", error);
        }
      });
    };
    $[37] = isDesktopApp;
    $[38] = showNotification;
    $[39] = user;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const catchUpMissed = t20;
  let t21;
  if ($[41] !== isDesktopApp || $[42] !== showNotification || $[43] !== userId) {
    t21 = event_2 => {
      if (event_2.type === REALTIME[REALTIME_KEYS.DESKTOP_NOTIFICATION].eventType) {
        clientLogger.info("GlobalRealtimeSubscriptions: notification received", event_2);
        if (isDesktopApp) {
          showNotificationOnce(userId, event_2.payload, showNotification);
          return;
        }
        showNotification(event_2.payload);
      }
    };
    $[41] = isDesktopApp;
    $[42] = showNotification;
    $[43] = userId;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = Boolean(user?.id);
  let t23;
  if ($[45] !== catchUpMissed || $[46] !== t21 || $[47] !== t22 || $[48] !== userId) {
    t23 = {
      onEvent: t21,
      onSubscribed: catchUpMissed,
      eventKey: REALTIME_KEYS.DESKTOP_NOTIFICATION,
      id: userId,
      enabled: t22
    };
    $[45] = catchUpMissed;
    $[46] = t21;
    $[47] = t22;
    $[48] = userId;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  useRealtime(t23);
  return null;
}
function _temp2(inst) {
  return inst.installationId.toString();
}
function _temp(state) {
  return state.selectedInstallationId;
}